<template>
  <Component :is="modalConfig.component" is-global :navigate-to="modalConfig.navigateTo" />
</template>
<script>
import { Portal } from '@linusborg/vue-simple-portal'
import { addScrollableSelector, clearQueueScrollLocks, disablePageScroll, enablePageScroll, removeScrollableSelector } from 'scroll-lock'
import { mapActions, mapGetters } from 'vuex'
import { globalModalsConfig } from '@/components/Base/Modal/Global/config'

const QUERY_KEY = 'modal'
const SCROLLABLE_SELECTOR = '.base-modal__body, .base-modal__body *'

export default {
  name: 'BaseModalGlobal',
  components: {
    Portal,
    SupportGlobalModal: () => import('@/components/GlobalModals/SupportGlobalModal'),
    AccountBlockedGlobalModal: () => import('@/components/GlobalModals/AccountBlockedGlobalModal'),
    UserHasBeenLoggedOutGlobalModal: () => import('@/components/GlobalModals/UserHasBeenLoggedOutGlobalModal'),
    PromocodeGlobalModal: () => import('@/components/GlobalModals/PromocodeGlobalModal'),
    RedirectVerificationGlobalModal: () => import('@/components/GlobalModals/RedirectVerificationGlobalModal'),
    RedirectReVerificationGlobalModal: () => import('@/components/GlobalModals/RedirectReVerificationGlobalModal'),
    RedirectVerificationErrorGlobalModal: () => import('@/components/GlobalModals/RedirectVerificationErrorGlobalModal'),
    VerificationBonusesGlobalModal: () => import('@/components/GlobalModals/VerificationBonusesGlobalModal'),
    VerifyBeforePayGlobalModal: () => import('@/components/GlobalModals/VerifyBeforePayGlobalModal'),
    LimitsModalBeforeGame: () => import('@/components/Limits/Modal/LimitsModalBeforeGame'),
    /** !!!__________always add no-portal to inner modal____________!!! */
  },
  computed: {
    ...mapGetters('user', { isLoggedIn: 'isLoggedIn' }),
    ...mapGetters('globalModal', {
      modalConfig: 'get_globalModalConfig',
      get_globalModalProps: 'get_globalModalProps',
      get_globalModalIsOpen: 'get_globalModalIsOpen',
    }),
    c_queryKey() {
      if (this.modalConfig && this.modalConfig.queryKey) return this.modalConfig.queryKey
      return QUERY_KEY
    },
  },
  watch: {
    '$route.path'(newVal, oldVal) {
      if (this.modalConfig.component && newVal !== oldVal) {
        this.close_globalModal()
      }
    },
    get_globalModalIsOpen() {
      if (this.get_globalModalIsOpen) {
        addScrollableSelector(SCROLLABLE_SELECTOR)
        disablePageScroll()
        this.addQuery()
      }
      else {
        this.enableScroll()
        this.removeQuery()
        this.reset_globalModal()
      }
    },
  },
  created() {
    if (process.server) return
    this.initByQuery()
  },
  mounted() {
    window.addEventListener('popstate', this.close_globalModal)
  },
  beforeDestroy() {
    this.close_globalModal()
    window.removeEventListener('popstate', this.close_globalModal)
    clearQueueScrollLocks()
  },
  methods: {
    ...mapActions('globalModal', {
      close_globalModal: 'close_globalModal',
      reset_globalModal: 'reset_globalModal',
    }),
    /** Query */
    getModalConfigBasedOnRouter() {
      return Object.values(globalModalsConfig)
        .find(configObj => {
          return this.$route.query[QUERY_KEY] === configObj.query
            || this.$route.query.hasOwnProperty(configObj.queryKeyOnly)
            || this.$route.query.hasOwnProperty(configObj.queryKey)
        })
    },
    async initByQuery() {
      /** Prepare */
      const modalConfig = this.getModalConfigBasedOnRouter()
      /** Guard */
      if (!modalConfig) return
      if (modalConfig.private && !this.isLoggedIn) {
        await this.$nextTick()
        this.$newModal.openAuthOrRegister()
        return
      }
      /** Action */
      await this.$store.dispatch('globalModal/open_globalModal', { config: modalConfig })
    },
    async addQuery() {
      /** Guard */
      if (
        !this.modalConfig
        || (!this.modalConfig?.query && !this.modalConfig?.queryKeyOnly)
        || (!!this.modalConfig?.query && this.$route.query[this.c_queryKey] === this.modalConfig?.query)
        || (!!this.modalConfig?.queryKeyOnly && this.$route.query.hasOwnProperty(this.modalConfig?.queryKeyOnly))
        || !!(
          this.modalConfig?.queryKey
            && this.$route.query.hasOwnProperty(this.modalConfig?.queryKey)
            && this.$route.query[this.modalConfig?.queryKey] === this.modalConfig?.query
        )
      ) return
      /** Logic */
      const oldQuery = { ...this.$route.query }
      if (this.modalConfig?.query) {
        oldQuery[this.c_queryKey] = this.modalConfig?.query
      }
      else if (this.modalConfig?.queryKeyOnly) {
        delete oldQuery[this.c_queryKey]
        oldQuery[this.modalConfig?.queryKeyOnly] = null
      }
      else {
        console.warn('addQuery validation fail')
        return
      }
      await this.$router.replace({ query: oldQuery })
    },
    async removeQuery(modalConfig = this.modalConfig) {
      /** Guard */
      if (
        !modalConfig
        || (!modalConfig?.query && !modalConfig?.queryKeyOnly)
        || (!this.$route.query[this.c_queryKey] && !this.$route.query.hasOwnProperty(modalConfig.queryKeyOnly))
      ) return
      /** Logic */
      if (modalConfig.removeAllQuery) {
        return await this.$router.replace({ query: {} })
      }
      const oldQuery = { ...this.$route.query }
      delete oldQuery[this.c_queryKey]
      delete oldQuery[modalConfig.queryKeyOnly]
      this.enableScroll()
      await this.close_globalModal()
      await this.$router.replace({ query: oldQuery })
    },
    /** Scroll */
    enableScroll() {
      enablePageScroll()
    },
  },
}
</script>
